import { useEffect, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import type { TFunction } from "i18next";
import Layout, { stepHeaderWrapperClass } from "../layout/Layout";
import BookingStepHeader from "../bookingForm/BookingStepHeader";
import BookingInfoInline from "../bookingForm/BookingInfoInline";
import MobileStepNav from "../bookingForm/MobileStepNav";
import * as rpc from "../../../common/jsonRpcRoutes";
import type { Booking, ManageOrderData } from "../../../common/jsonRpcTypes";
import OrderList from "./OrderList";
import OrderSummary from "./OrderSummary";
import PaymentBreakdown from "./PaymentBreakdown";
import PaymentStage from "./PaymentStage";
import PaymentConfirmationLayout from "../confirmation/PaymentConfirmationLayout";
import PrimaryButton from "../buttons/primaryButton.tsx";
import { CTA_FULL_WIDTH } from "../buttons/ctaClassNames";
import { CheckCircleFill, Clock } from "../icons";
import { formatTime, formatCurrency, formatBookingDate } from "../../../utils/countryLocaleMap";
import { useLegacyPreorderRedirect } from "../../utils/redirectToOldExperience";
import { hasBookingTakenPlace } from "../../utils/pastBooking";
import { refusalMessage } from "../../utils/rpcRefusal";
import AlertBanner from "../shared/AlertBanner";
import PageStateGuard from "../shared/PageStateGuard";
import { useVenueCurrency } from "../hooks/useVenueCurrency";

interface ManageYourOrderProps {
  bookingId: string;
  signature?: string;
  placeOrderUrl?: string;
}

type Stage = "summary" | "payment" | "completion";

export function buildPlaceOrderUrl(
  bookingId: string,
  signature: string,
  placeOrderUrl?: string,
  orderId?: string,
): string | null {
  if (!placeOrderUrl) {
    const params = new URLSearchParams({ booking_id: bookingId });
    if (orderId) params.set("order_id", orderId);
    params.set("signature", signature);
    return `/place-your-order?${params.toString()}`;
  }

  try {
    const url = new URL(placeOrderUrl, window.location.origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    url.searchParams.set("booking_id", bookingId);
    url.searchParams.set("signature", signature);
    if (orderId) url.searchParams.set("order_id", orderId);
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The booking date, preferring the booking payload and falling back to the
 * order data, which carries it even when DMN refuses the booking itself.
 */
function resolveBookingDate(
  bookingResult: PromiseSettledResult<Booking>,
  orderDataResult: PromiseSettledResult<ManageOrderData>,
): string | undefined {
  if (bookingResult.status === "fulfilled" && typeof bookingResult.value?.date === "string") {
    return bookingResult.value.date;
  }
  if (orderDataResult.status === "fulfilled") return orderDataResult.value?.booking?.date;
  return undefined;
}

const pastBookingMessage = (t: TFunction): string =>
  t("booking_past_no_preorders", "This booking has already taken place, so no more pre-orders can be added.");

const failureMessage = (failure: unknown): string =>
  failure instanceof Error ? failure.message : "Failed to load order data";

/** What the two loads leave the page able to show, and why it cannot show more. */
interface LoadOutcome {
  /** An expected outcome to explain, which no amount of retrying will change. */
  refusalMessage: string | null;
  /** A failure worth reporting and retrying. */
  error: string | null;
}

/**
 * Judge each load on its own terms.
 *
 * The order data *is* this page, so any failure to fetch it is a fault the
 * guest should be able to retry — never a notice about the booking. Only a
 * refusal of the booking itself is an expected outcome, and only then is a
 * booking whose date has passed explained as one that has taken place.
 *
 * Note the two `status` fields in play: a settled result's own
 * `"fulfilled" | "rejected"`, and the booking's status inside `value`, which is
 * a workflow state and says nothing about whether the visit is over.
 */
function describeLoad(
  bookingResult: PromiseSettledResult<Booking>,
  orderDataResult: PromiseSettledResult<ManageOrderData>,
  t: TFunction,
): LoadOutcome {
  const bookingRefusal = bookingResult.status === "rejected" ? refusalMessage(bookingResult.reason) : null;
  const bookingIsPast = hasBookingTakenPlace({
    date: resolveBookingDate(bookingResult, orderDataResult),
    refused: bookingRefusal !== null,
  });

  if (orderDataResult.status === "rejected") {
    return { refusalMessage: null, error: failureMessage(orderDataResult.reason) };
  }

  if (bookingRefusal) {
    return { refusalMessage: bookingIsPast ? pastBookingMessage(t) : bookingRefusal, error: null };
  }

  if (bookingResult.status === "rejected") {
    return { refusalMessage: null, error: failureMessage(bookingResult.reason) };
  }

  return { refusalMessage: null, error: null };
}

const SummaryStage: React.FC<{
  bookingData: Booking;
  manageOrderData: ManageOrderData;
  manageFormattedDate: string | undefined;
  manageFormattedTime: string | undefined;
  onBackToSummary?: () => void;
  onPreOrder: () => void;
  stage: Stage;
}> = ({
  bookingData,
  manageOrderData,
  manageFormattedDate,
  manageFormattedTime,
  onBackToSummary,
  onPreOrder,
  stage,
}) => {
  const { t } = useTranslation();
  const acceptsPreorders = !manageOrderData.preorderSettings.complete;

  return (
    <div id="gc-myo-summary-stage">
      <div className={stepHeaderWrapperClass}>
        <BookingStepHeader venueName={bookingData.venue.title} onBack={onBackToSummary} />
      </div>
      <BookingInfoInline
        venueName={bookingData.venue.title}
        bookingDate={manageFormattedDate}
        bookingTime={manageFormattedTime}
        covers={String(bookingData.guestCount ?? 0)}
        bookingTypeName={bookingData.bookingTypeName}
      />
      <div className="app-main">
        <div className="gcw:mb-2">
          <h1 className="payment-title">{t("manage_order_title", "Manage Your Order")}</h1>
          <p className="gcw:text-order-details">
            {manageOrderData.preorderSettings.complete
              ? t("orders_added_to_booking", "The following orders have been added to your booking:")
              : t(
                  "manage_order_subtitle",
                  "Add items to your pre-order using the button below, then return here to review and complete.",
                )}
          </p>

          {/* `success` carries the marker the design's flat green panel keys
              off. AlertBanner only applies its default top margin to an
              unvaried banner, so the gap this one has always had is restated. */}
          {stage === "summary" && manageOrderData.preorderSettings.complete && (
            <AlertBanner
              variant="success"
              className="payment-complete-alert gcw:mt-2"
              icon={<CheckCircleFill size={20} color="#09A57F" />}
              translatedKey="preorders_marked_complete"
              textClassName="alert-text"
            />
          )}

          {acceptsPreorders && (
            <div className="gcw:mt-bs-3">
              <PrimaryButton
                text={t("pre_order_items_now", "Pre-order items")}
                className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-accent-fg gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:text-lg ${CTA_FULL_WIDTH}`}
                onClick={onPreOrder}
              />
            </div>
          )}

          {stage === "summary" && acceptsPreorders && manageOrderData.preorderSettings.close_date && (
            <div className="gcw:flex gcw:items-center gcw:flex-row gcw:mt-bs-3 close-text">
              <Clock color="#007373" size={16} className="gcw:me-2" />
              <Trans
                i18nKey="bookings_close_on_date"
                values={{
                  date: formatBookingDate(
                    manageOrderData.preorderSettings.close_date,
                    bookingData.venue.country_code ?? "GB",
                    "long",
                    bookingData.venue.timezone,
                  ),
                }}
                components={{ strong: <strong /> }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentStageWrapper: React.FC<{
  bookingId: string;
  manageOrderData: ManageOrderData;
  bookingData: Booking;
  onPaymentSuccess: (paymentDetails?: unknown) => void;
  onBack: () => void;
  submitting: boolean;
}> = ({ bookingId, manageOrderData, bookingData, onPaymentSuccess, onBack, submitting }) => {
  return (
    <div className="manage-order-section">
      <PaymentStage
        bookingId={bookingId}
        manageOrderData={manageOrderData}
        bookingData={bookingData}
        onPaymentSuccess={onPaymentSuccess}
        onBack={onBack}
        submitting={submitting}
      />
    </div>
  );
};

const CompletionStage: React.FC<{
  bookingData: Booking;
  paymentAmount: number;
  manageOrderData: ManageOrderData | null;
}> = ({ bookingData, paymentAmount, manageOrderData }) => {
  // Determine the correct mode based on manageOrderData
  const hasPreorderItems = manageOrderData?.orders && manageOrderData.orders.length > 0;
  const isPreorderCompletion = hasPreorderItems;

  // Determine mode: if there are preorder items, show success with booking details, otherwise success only
  const mode = hasPreorderItems ? "successWithDetails" : "successOnly";

  return (
    <div className="manage-order-section gcw:mt-bs-4 gcw:ms-bs-4 gcw:me-bs-4">
      <PaymentConfirmationLayout
        venueName={bookingData.venue.title}
        bookingRef={bookingData.bookingRef}
        bookingType={bookingData.bookingTypeName}
        depositTotal={paymentAmount}
        isAuthentication={false}
        partySize={bookingData.guestCount}
        date={
          formatBookingDate(
            bookingData.date && typeof bookingData.date === "string" ? bookingData.date : null,
            bookingData.venue.country_code ?? "GB",
            "long",
            bookingData.venue.timezone,
          ) || ""
        }
        time={
          bookingData.time && bookingData.date && typeof bookingData.date === "string"
            ? formatTime(bookingData.time, bookingData.date, bookingData.venue.country_code ?? "GB")
            : bookingData.time
        }
        location={bookingData.venue.title}
        currency={bookingData.venue.currency}
        countryCode={bookingData.venue.country_code}
        preOrderItems={manageOrderData?.orders.flatMap((order) => order.items)}
        mode={mode}
        hideHeader={false}
        isPreorderCompletion={isPreorderCompletion || undefined}
        featureName="Preorder"
        userId={bookingData.user?.user_id ?? null}
        customerName={bookingData.user?.user_name ?? null}
      />
    </div>
  );
};

const ManageYourOrder: React.FC<ManageYourOrderProps> = ({ bookingId, signature: signatureProp, placeOrderUrl }) => {
  const [bookingData, setBookingData] = useState<Booking | null>(null);
  const [manageOrderData, setManageOrderData] = useState<ManageOrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("summary");
  const [serviceChargePercentage, setServiceChargePercentage] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  /** Identifies the newest load, so an earlier one cannot commit over it. */
  const loadToken = useRef(0);
  /** The booking on screen right now, for work that outlives a change of it. */
  const currentBookingId = useRef(bookingId);
  currentBookingId.current = bookingId;
  const { t } = useTranslation();
  const { currency, countryCode } = useVenueCurrency(bookingData?.venue);
  const isRedirecting = useLegacyPreorderRedirect(bookingData, bookingId);

  /**
   * Whether work started for `id` may still commit. A load or a mutation that
   * outlives a change of booking must not put one booking's orders on screen
   * while the handlers act on another.
   */
  const isCurrentBooking = (id: string) => currentBookingId.current === id;

  useEffect(() => {
    if (!bookingId) return;

    // Nothing of the previous booking may be left on screen: its orders would
    // otherwise be shown while every action targets the new booking.
    setBookingData(null);
    setManageOrderData(null);
    setError(null);
    setRefusalMessage(null);
    setStage("summary");
    setServiceChargePercentage(0);
    loadData();
  }, [bookingId]);

  const loadData = async () => {
    // Loads are not ordered either, so within one booking the newest wins.
    const requestedBookingId = bookingId;
    loadToken.current += 1;
    const token = loadToken.current;
    const isCurrent = () => loadToken.current === token && isCurrentBooking(requestedBookingId);

    setLoading(true);
    setError(null);
    setRefusalMessage(null);

    try {
      // A booking DMN refuses to hand over must not cost the guest the order
      // data, which comes from a different endpoint and is often still there.
      const [bookingResult, orderDataResult] = await Promise.allSettled([
        rpc.call("/getBooking", { booking_id: bookingId }),
        rpc.call("/getManageCustomerPreorderDetails", { booking_id: bookingId }),
      ]);

      if (!isCurrent()) return;

      if (bookingResult.status === "fulfilled") setBookingData(bookingResult.value);
      if (orderDataResult.status === "fulfilled") {
        setManageOrderData(orderDataResult.value);
        setServiceChargePercentage(orderDataResult.value?.serviceChargePercentage || 0);
      }

      const outcome = describeLoad(bookingResult, orderDataResult, t);
      setRefusalMessage(outcome.refusalMessage);
      setError(outcome.error);
    } catch (err) {
      if (isCurrent()) setError(failureMessage(err));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const navigateToPlaceOrder = (orderId?: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    const signature = signatureProp ?? currentParams.get("signature");
    const navigationBookingId = placeOrderUrl ? bookingId : currentParams.get("booking_id");
    const url =
      signature && navigationBookingId
        ? buildPlaceOrderUrl(navigationBookingId, signature, placeOrderUrl, orderId)
        : null;
    if (!url) return;

    try {
      sessionStorage.setItem("navigation_source", "manage-your-order");
    } catch (error) {
      console.log("SessionStorage not available:", error);
    }
    window.open(url, "_parent");
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (!confirm(t("confirm_delete_order", "Are you sure you want to delete this order?"))) {
      return;
    }

    const requestedBookingId = bookingId;

    try {
      setSubmitting(true);
      await rpc.call("/deleteCustomerPreorderOrder", {
        booking_id: requestedBookingId,
        order_id: orderId,
      });
      if (!isCurrentBooking(requestedBookingId)) return;
      await loadData();
    } catch (err) {
      if (isCurrentBooking(requestedBookingId)) setError(err instanceof Error ? err.message : "Failed to delete order");
    } finally {
      if (isCurrentBooking(requestedBookingId)) setSubmitting(false);
    }
  };

  const handleEditOrder = (orderId: string) => navigateToPlaceOrder(orderId);

  const handleCompletePreorders = async (paymentDetails?: unknown) => {
    const requestedBookingId = bookingId;

    try {
      setSubmitting(true);
      const result = await rpc.call("/completePreorders", {
        booking_id: requestedBookingId,
        paymentDetails,
        serviceChargePercentage,
      });

      if (!isCurrentBooking(requestedBookingId)) return;

      if (result.success) {
        setPaymentAmount(result.paymentReceived?.amount ?? 0);
        await loadData();
        if (isCurrentBooking(requestedBookingId)) setStage("completion");
      }
    } catch (err) {
      if (isCurrentBooking(requestedBookingId)) {
        setError(err instanceof Error ? err.message : "Failed to complete preorders");
      }
    } finally {
      if (isCurrentBooking(requestedBookingId)) setSubmitting(false);
    }
  };

  const canCompletePreOrder = () => {
    if (!manageOrderData) return false;
    const hasOrders = manageOrderData.orders.some((order) => order.status === "pending");
    return hasOrders && !submitting && isMinGroupMemberOrdersMet();
  };

  const isMinGroupMemberOrdersMet = () => {
    if (!manageOrderData) return true;
    const { min_group_member_orders, current_group_member_orders } = manageOrderData.preorderSettings;
    if (min_group_member_orders === undefined || min_group_member_orders === null) return true;
    const currentCount = current_group_member_orders ?? 0;
    return currentCount >= min_group_member_orders;
  };

  const formatBookingDetails = (booking: Booking) => {
    const manageCountryCode = booking.venue?.country_code ?? "GB";
    const manageFormattedDate =
      booking.date && typeof booking.date === "string"
        ? formatBookingDate(booking.date, manageCountryCode, "short")
        : undefined;
    const manageFormattedTime =
      booking.time && booking.date && typeof booking.date === "string"
        ? formatTime(booking.time, booking.date, manageCountryCode)
        : undefined;
    return { manageFormattedDate, manageFormattedTime };
  };

  if (loading || isRedirecting || refusalMessage || error || !bookingData || !manageOrderData) {
    return (
      <PageStateGuard
        loading={loading || isRedirecting}
        error={error}
        refusalMessage={refusalMessage}
        data={bookingData && manageOrderData}
        noDataMessage={t("noBookingData", "No booking data found")}
        showHeader
        errorAction={
          <PrimaryButton
            type="button"
            className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-accent-fg gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
            onClick={loadData}
            text={t("retry", "Retry")}
          />
        }
      >
        {null}
      </PageStateGuard>
    );
  }

  const { manageFormattedDate, manageFormattedTime } = formatBookingDetails(bookingData);

  // Mobile back button rendered inside the BookingHeader on small screens, mirroring the
  // desktop BookingStepHeader back button. Only the payment stage can step back (to summary);
  // the summary and completion stages have no header back navigation on either breakpoint.
  const mobileBackNav =
    stage === "payment" ? (
      <MobileStepNav
        currentStepInfo={undefined}
        currentStepNumber={0}
        totalStepCount={0}
        onBack={() => setStage("summary")}
      />
    ) : null;

  return (
    <Layout
      showHeader
      headerChildren={mobileBackNav}
      mainClassName={stage === "completion" ? "" : "manage-order-page"}
      main={
        <>
          <div className="manage-order-section">
            {stage !== "completion" && (
              <SummaryStage
                bookingData={bookingData}
                manageOrderData={manageOrderData}
                manageFormattedDate={manageFormattedDate}
                manageFormattedTime={manageFormattedTime}
                onBackToSummary={stage === "payment" ? () => setStage("summary") : undefined}
                onPreOrder={() => navigateToPlaceOrder()}
                stage={stage}
              />
            )}
          </div>

          {stage === "summary" && (
            <>
              <div className="manage-order-section">
                <div className="app-main">
                  <OrderList
                    orders={manageOrderData.orders}
                    onDeleteOrder={handleDeleteOrder}
                    onEditOrder={handleEditOrder}
                    submitting={submitting}
                    currency={currency}
                    countryCode={countryCode}
                    hidePaid={manageOrderData.leadBookerPaidAmount > 0}
                  />
                </div>
              </div>

              {manageOrderData.orders.length > 0 && (
                <div className="manage-order-section">
                  <div className="app-main">
                    <PaymentBreakdown
                      groupMemberPaidAmount={manageOrderData.groupMemberPaidAmount}
                      leadBookerPaidAmount={manageOrderData.leadBookerPaidAmount}
                      currency={currency}
                      countryCode={countryCode}
                      formatCurrency={formatCurrency}
                    />
                  </div>
                </div>
              )}

              {manageOrderData.orders.some((order) => order.status === "pending") &&
                !(manageOrderData.preorderSettings.complete && manageOrderData.requiredPaymentAmount === 0) && (
                  <div className="manage-order-section">
                    <div className="app-main">
                      <OrderSummary
                        manageOrderData={manageOrderData}
                        onCompletePreorders={() => {
                          if (manageOrderData?.requiredPaymentAmount && manageOrderData.requiredPaymentAmount > 0) {
                            setStage("payment");
                          } else {
                            handleCompletePreorders();
                          }
                        }}
                        canComplete={canCompletePreOrder()}
                        submitting={submitting}
                        minGroupMemberOrdersNotMet={!isMinGroupMemberOrdersMet()}
                      />
                    </div>
                  </div>
                )}
            </>
          )}

          {stage === "payment" && (
            <PaymentStageWrapper
              bookingId={bookingId}
              manageOrderData={manageOrderData}
              bookingData={bookingData}
              onPaymentSuccess={handleCompletePreorders}
              onBack={() => setStage("summary")}
              submitting={submitting}
            />
          )}

          {stage === "completion" && (
            <CompletionStage
              bookingData={bookingData}
              paymentAmount={paymentAmount}
              manageOrderData={manageOrderData}
            />
          )}
        </>
      }
      aside={null}
    />
  );
};

export { ManageYourOrder };
